import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Calendar, MapPin, ArrowLeft } from 'lucide-react';
import { Card, Button } from '@caruso/ui';
import { eventsService } from '@/services/events.service';
import { useEffect, useState } from 'react';

export function ConfirmEventPage() {
  const { slug, token } = useParams<{ slug: string; token: string }>();
  const [confirmed, setConfirmed] = useState(false);

  const confirmMutation = useMutation({
    mutationFn: () => eventsService.confirmGuest(token!),
    onSuccess: () => {
      setConfirmed(true);
    },
  });

  // Fetch event details for display
  const { data: event } = useQuery({
    queryKey: ['public-event', slug],
    queryFn: () => eventsService.getBySlug(slug!),
    enabled: !!slug,
  });

  useEffect(() => {
    // Auto-confirm on page load
    if (token && !confirmed && !confirmMutation.isPending && !confirmMutation.isSuccess) {
      confirmMutation.mutate();
    }
  }, [token]);

  if (confirmMutation.isPending) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center">
        <div className="text-white text-xl">Confirmando presença...</div>
      </div>
    );
  }

  if (confirmMutation.isError) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center p-4">
        <Card className="p-8 bg-brand-smoke border-brand-gold/20 max-w-md text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Erro ao confirmar presença</h1>
          <p className="text-brand-ivory/70 mb-6">
            Não foi possível confirmar sua presença. O link pode estar inválido ou você já respondeu este convite.
          </p>
          {event && (
            <Link to={`/evento/${slug}`}>
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Ver detalhes do evento
              </Button>
            </Link>
          )}
        </Card>
      </div>
    );
  }

  if (confirmMutation.isSuccess && confirmMutation.data) {
    const { guest, event: eventData } = confirmMutation.data;
    const eventDate = new Date(eventData.eventDate);
    const formattedDate = eventDate.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center p-4">
        <Card className="p-8 bg-brand-smoke border-brand-gold/20 max-w-md text-center">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>

          <h1 className="text-3xl font-bold text-white mb-2">Presença Confirmada!</h1>
          <p className="text-xl text-brand-gold mb-8">Obrigado, {guest.name}!</p>

          <div className="bg-brand-ink/50 rounded-lg p-6 mb-8 text-left space-y-4">
            <h2 className="text-lg font-semibold text-white mb-4">{eventData.name}</h2>

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-brand-gold mt-0.5" />
              <div>
                <p className="text-brand-ivory/60 text-sm">Data e Horário</p>
                <p className="text-white">{formattedDate}</p>
              </div>
            </div>

            {eventData.location && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-brand-gold mt-0.5" />
                <div>
                  <p className="text-brand-ivory/60 text-sm">Local</p>
                  <p className="text-white">{eventData.location}</p>
                </div>
              </div>
            )}
          </div>

          <p className="text-brand-ivory/70 mb-6">
            Você receberá mais informações em breve. Aguardamos você!
          </p>

          <Link to={`/evento/${eventData.slug}`}>
            <Button className="w-full" variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Ver detalhes do evento
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return null;
}
